<template>
  <div>
    <Breadcrumbs main="Court" title="Documents" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Documents</h5>
                <span class="text-muted">Upload and manage case documents</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadDocuments"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Upload Document
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading documents...
              </div>
              <div v-else-if="sortedDocuments.length === 0" class="text-center py-4 text-muted">
                No documents found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Matter</th>
                      <th>Version</th>
                      <th>Uploaded by</th>
                      <th>Upload date</th>
                      <th>File</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="doc in sortedDocuments" :key="doc.documentId">
                      <td>{{ doc.documentId }}</td>
                      <td><strong>{{ doc.documentName || '—' }}</strong></td>
                      <td>
                        <span v-if="doc.documentType" class="badge badge-light-primary">
                          {{ doc.documentType }}
                        </span>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>{{ matterLabel(doc.matterId) }}</td>
                      <td>{{ doc.versionNo ?? '—' }}</td>
                      <td>{{ doc.uploadedBy || '—' }}</td>
                      <td>{{ formatDateTime(doc.uploadDate) }}</td>
                      <td class="text-truncate" style="max-width: 160px" :title="doc.filePath || ''">
                        {{ doc.filePath || '—' }}
                      </td>
                      <td class="text-end text-nowrap">
                        <button
                          v-if="doc.filePath"
                          class="btn btn-sm btn-outline-secondary me-1"
                          type="button"
                          title="Preview"
                          @click="openPreview(doc)"
                        >
                          <vue-feather type="eye" />
                        </button>
                        <button
                          v-if="doc.filePath"
                          class="btn btn-sm btn-outline-secondary me-1"
                          type="button"
                          title="Download"
                          :disabled="downloadingId === doc.documentId"
                          @click="downloadDocument(doc)"
                        >
                          <vue-feather type="download" />
                        </button>
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(doc)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(doc)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Document' : 'Upload Document' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>

                <div class="row">
                  <div class="col-12">
                    <div class="form-group form-field mb-3">
                      <label class="form-label">
                        File
                        <span v-if="!isEditing" class="text-danger">*</span>
                      </label>
                      <input
                        ref="fileInput"
                        class="form-control"
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp"
                        @change="onFileSelected"
                      >
                      <small class="text-muted">Maximum file size 50 MB</small>
                      <div v-if="selectedFile" class="mt-2 p-2 bg-light rounded">
                        <strong>{{ selectedFile.name }}</strong>
                        <span class="text-muted ms-2">({{ formatFileSize(selectedFile.size) }})</span>
                        <button
                          type="button"
                          class="btn btn-link btn-sm text-danger p-0 ms-2"
                          @click="clearSelectedFile"
                        >
                          Remove
                        </button>
                      </div>
                      <div v-else-if="isEditing && editingFilePath" class="mt-2 p-2 bg-light rounded">
                        <span class="text-muted">Current file:</span>
                        <strong class="ms-1">{{ editingFilePath }}</strong>
                        <p class="text-muted f-12 mb-0 mt-1">
                          Choose a new file above to replace the stored document.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-8">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Document name</label>
                      <input
                        v-model="form.documentName"
                        class="form-control"
                        type="text"
                        maxlength="255"
                        placeholder="e.g. Plaint.pdf"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Document type</label>
                      <select v-model="form.documentType" class="form-select">
                        <option value="">Select type</option>
                        <option v-for="type in documentTypeOptions" :key="type" :value="type">
                          {{ type }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Linked matter</label>
                      <select v-model.number="form.matterId" class="form-select">
                        <option :value="null">None</option>
                        <option
                          v-for="matter in matters"
                          :key="matter.matterId"
                          :value="matter.matterId"
                        >
                          {{ matterLabel(matter.matterId) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Version</label>
                      <input
                        v-model.number="form.versionNo"
                        class="form-control"
                        type="number"
                        min="1"
                        step="1"
                      >
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Uploaded by</label>
                      <input
                        v-model="form.uploadedBy"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. Jane Doe"
                      >
                    </div>
                  </div>
                  <div v-if="isEditing && editingUploadDate" class="col-12">
                    <p class="text-muted f-12 mb-0">
                      Last upload date: {{ formatDateTime(editingUploadDate) }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Upload Document') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="previewDoc"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ previewDoc.documentName || `Document #${previewDoc.documentId}` }}
              </h5>
              <button type="button" class="btn-close" aria-label="Close" @click="closePreview" />
            </div>
            <div class="modal-body">
              <div v-if="previewLoading" class="text-center py-5 text-muted">
                Loading preview...
              </div>
              <div v-else-if="previewError" class="alert alert-danger" role="alert">
                {{ previewError }}
              </div>
              <template v-else-if="previewUrl">
                <iframe
                  v-if="previewMode === 'pdf'"
                  :src="previewUrl"
                  class="document-preview-frame w-100 border-0"
                  title="Document preview"
                />
                <div v-else-if="previewMode === 'image'" class="text-center">
                  <img
                    :src="previewUrl"
                    :alt="previewDoc.documentName || 'Document preview'"
                    class="document-preview-image img-fluid"
                  >
                </div>
                <div v-else class="text-center py-5">
                  <vue-feather type="file" class="mb-3" style="width: 48px; height: 48px" />
                  <p class="mb-2">Preview is not available for this file type.</p>
                  <p class="text-muted f-12 mb-3">{{ previewDoc.filePath }}</p>
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="downloadDocument(previewDoc)"
                  >
                    <vue-feather type="download" class="me-1" />
                    Download file
                  </button>
                </div>
              </template>
            </div>
            <div class="modal-footer">
              <button
                v-if="previewDoc.filePath"
                type="button"
                class="btn btn-outline-primary"
                :disabled="downloadingId === previewDoc.documentId"
                @click="downloadDocument(previewDoc)"
              >
                <vue-feather type="download" class="me-1" />
                Download
              </button>
              <button type="button" class="btn btn-secondary" @click="closePreview">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="previewDoc" class="modal-backdrop fade show" @click="closePreview" />
    </teleport>

    <teleport to="body">
      <div
        v-if="documentToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Document</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="documentToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete
                <strong>{{ documentToDelete.documentName || `Document #${documentToDelete.documentId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The document record will be soft-deleted and hidden from the list. The file on disk is retained.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="documentToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="documentToDelete" class="modal-backdrop fade show" @click="documentToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listMatters } from '@/services/matterService'
import {
  listDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  previewDocument,
} from '@/services/documentService'

const DOCUMENT_TYPE_OPTIONS = ['PLEADING', 'CONTRACT', 'CORRESPONDENCE', 'EVIDENCE', 'ORDER', 'OTHER']
const MAX_FILE_SIZE = 50 * 1024 * 1024

function hasDocumentPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const priv = user.privileges.find((p) => p.entity === 'DOCUMENT')
  if (!priv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return priv[map[action]] === true
}

function fileExtension(name) {
  if (!name) return ''
  const parts = name.split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

function previewModeFor(mimeType, fileName) {
  if (mimeType === 'application/pdf' || fileExtension(fileName) === 'pdf') return 'pdf'
  if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension(fileName))) {
    return 'image'
  }
  return 'other'
}

const emptyForm = () => ({
  matterId: null,
  documentName: '',
  documentType: '',
  versionNo: 1,
  uploadedBy: '',
})

export default {
  name: 'CourtDocuments',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasDocumentPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasDocumentPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasDocumentPrivilege(this.currentUser, 'delete')
    },

    documentTypeOptions() {
      return DOCUMENT_TYPE_OPTIONS
    },

    sortedDocuments() {
      return [...this.documents].sort((a, b) => {
        const dateA = a.uploadDate || ''
        const dateB = b.uploadDate || ''
        if (dateA !== dateB) return dateB.localeCompare(dateA)
        return (b.documentId || 0) - (a.documentId || 0)
      })
    },
  },

  data() {
    return {
      documents: [],
      matters: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      editingUploadDate: null,
      editingFilePath: '',
      selectedFile: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      documentToDelete: null,
      actionError: '',
      actionLoading: false,
      previewDoc: null,
      previewUrl: '',
      previewMode: 'other',
      previewLoading: false,
      previewError: '',
      downloadingId: null,
    }
  },

  created() {
    this.loadDocuments()
    this.loadMatters()
  },

  beforeUnmount() {
    this.revokePreviewUrl()
  },

  methods: {
    async loadDocuments() {
      this.loading = true
      this.error = ''

      try {
        const data = await listDocuments()
        this.documents = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view documents.'
        } else {
          this.error = err.message || 'Failed to load documents'
        }
      } finally {
        this.loading = false
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    matterLabel(matterId) {
      if (!matterId) return '—'
      const matter = this.matters.find((m) => m.matterId === matterId)
      if (!matter) return `#${matterId}`
      return matter.caseTitle || matter.caseNo || `Matter #${matterId}`
    },

    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    onFileSelected(event) {
      const file = event.target.files?.[0]
      if (!file) {
        this.selectedFile = null
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        this.formError = 'File exceeds the 50 MB upload limit.'
        this.clearSelectedFile()
        return
      }

      this.formError = ''
      this.selectedFile = file

      if (!this.form.documentName?.trim()) {
        this.form.documentName = file.name
      }
    },

    clearSelectedFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.editingUploadDate = null
      this.editingFilePath = ''
      this.form = emptyForm()
      this.formError = ''
      this.clearSelectedFile()
      this.showFormModal = true
    },

    openEdit(doc) {
      this.isEditing = true
      this.editingId = doc.documentId
      this.editingUploadDate = doc.uploadDate || null
      this.editingFilePath = doc.filePath || ''
      this.form = {
        matterId: doc.matterId || null,
        documentName: doc.documentName || '',
        documentType: doc.documentType || '',
        versionNo: doc.versionNo ?? 1,
        uploadedBy: doc.uploadedBy || '',
      }
      this.formError = ''
      this.clearSelectedFile()
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
      this.clearSelectedFile()
    },

    buildPayload() {
      const payload = { file: this.selectedFile || undefined }

      const assignString = (key, value, maxLen) => {
        const trimmed = value?.trim()
        if (trimmed) payload[key] = maxLen ? trimmed.slice(0, maxLen) : trimmed
      }

      if (this.form.matterId) payload.matterId = this.form.matterId
      assignString('documentName', this.form.documentName, 255)
      assignString('documentType', this.form.documentType, 100)
      assignString('uploadedBy', this.form.uploadedBy, 100)

      if (this.form.versionNo != null && this.form.versionNo !== '') {
        payload.versionNo = Number(this.form.versionNo)
      }

      return payload
    },

    async submitForm() {
      this.formError = ''

      if (!this.isEditing && !this.selectedFile) {
        this.formError = 'Please choose a file to upload.'
        return
      }

      if (this.selectedFile && this.selectedFile.size > MAX_FILE_SIZE) {
        this.formError = 'File exceeds the 50 MB upload limit.'
        return
      }

      this.saving = true

      try {
        if (this.isEditing) {
          await updateDocument({
            documentId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createDocument(this.buildPayload())
        }
        this.showFormModal = false
        this.clearSelectedFile()
        await this.loadDocuments()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    revokePreviewUrl() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = ''
      }
    },

    async openPreview(doc) {
      this.previewDoc = doc
      this.previewLoading = true
      this.previewError = ''
      this.previewMode = 'other'
      this.revokePreviewUrl()

      try {
        const blob = await previewDocument(doc.documentId)
        this.previewUrl = URL.createObjectURL(blob)
        this.previewMode = previewModeFor(blob.type, doc.documentName || doc.filePath)
      } catch (err) {
        this.previewError = err.message || 'Failed to load document preview'
      } finally {
        this.previewLoading = false
      }
    },

    closePreview() {
      this.previewDoc = null
      this.previewError = ''
      this.previewLoading = false
      this.revokePreviewUrl()
    },

    async downloadDocument(doc) {
      this.downloadingId = doc.documentId

      try {
        const blob = await previewDocument(doc.documentId)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = doc.documentName || doc.filePath || `document-${doc.documentId}`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      } catch (err) {
        if (this.previewDoc?.documentId === doc.documentId) {
          this.previewError = err.message || 'Failed to download document'
        } else {
          this.error = err.message || 'Failed to download document'
        }
      } finally {
        this.downloadingId = null
      }
    },

    confirmDelete(doc) {
      this.documentToDelete = doc
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteDocument(this.documentToDelete.documentId)
        this.documentToDelete = null
        await this.loadDocuments()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete document'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped>
.document-preview-frame {
  min-height: 70vh;
}

.document-preview-image {
  max-height: 70vh;
  object-fit: contain;
}
</style>
